import { Fragment, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { classNames, getIconUrl } from '~/lib/util';
import { Token } from '~/lib/types';


type Props = {
   name: string
	tokens: Token[],
	selected: Token,
	onSelected: (value: Token) => void
}

export default function TokenSelector({ name, tokens, selected, onSelected }: Props) {
   const [displayTokens, setDisplayTokens] = useState(tokens);
   const [searchTerm, setSearchTerm] = useState('');

   function onSearch(term: string) {
      setSearchTerm(term);
   }

   useEffect(() => {
      setDisplayTokens(tokens);
   }, [tokens]);

   useEffect(() => {
      let regExSearch = new RegExp(searchTerm, 'i');

      if (!searchTerm) {
         setDisplayTokens(tokens);
         return;
      }

      let filteredTokens = [];
      for (let token of tokens) {
         if (token.symbol && token.symbol.match(regExSearch)) {
            filteredTokens.push(token);
            continue;
         }

         if (token.name && token.name.match(regExSearch)) {
            filteredTokens.push(token);
         }
      }

      setDisplayTokens(filteredTokens);
   }, [searchTerm]);

   return (
      <div className="grow mt-5 lg:mt-0 flex-none">
         <Listbox name={name} value={selected} onChange={onSelected}>
            {({ open }) => (
               <>
                  <div className="relative">
                     <Listbox.Button className="relative w-full text-black hover:text-primary-500 rounded-md pr-10 py-2 text-left focus:outline-3 focus:ring-1 focus:ring-primary-200 focus:border-primary-300 sm:text-sm">
                        <span className="flex items-center">
                           <img src={getIconUrl(selected.iconUrl)} alt="" className="shrink-0 h-8 w-8 rounded-full" />
                           <span className="ml-3 block text-xl font-medium">{selected.symbol}</span>
                           {/* <span className="ml-3 text-xs text-gray-500 truncate">{selected.name}</span> */}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center">
                           <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                     </Listbox.Button>

                     <Transition show={open} as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <Listbox.Options className="absolute right-0 z-10 mt-1 max-h-56 w-full lg:w-60 overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-hidden sm:text-sm">
                           <div key="search" className="z-10 sticky top-0 left-0 right-0">
                              <div className="flex items-center">
                                 <input
                                    type="search"
                                    value={searchTerm}
                                    onChange={(evt) => onSearch(evt.target.value)}
                                    autoFocus
                                    autoCorrect="false"
                                    onFocus={(e) => e.currentTarget.select()}
                                    placeholder="Search..."
                                    className="bg-transparent w-60 bg-white border-none text-black placeholder:text-slate-400 focus:ring-primary-300"
                                 />
                              </div>
                           </div>
                           {displayTokens.map((token) => (
                              <Listbox.Option key={token.address} value={token} className="text-black hover:text-primary-500 hover:bg-neutral-100 relative cursor-default select-none py-2 pl-3 pr-9">
                                 {({ selected }) => (
                                    <>
                                       <div className="flex items-center">
                                          <img src={getIconUrl(token.iconUrl)} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                                          <div className={classNames(selected ? 'font-semibold' : 'font-normal', 'ml-3 block truncate')}>
                                             <p className="title font-semibold">{token.symbol || 'Unknown'}</p>
                                             <p className="name text-xs text-gray-700">{token.name}</p>
                                          </div>
                                       </div>
                                    </>
                                 )}
                              </Listbox.Option>
                           ))}
                        </Listbox.Options>
                     </Transition>
                  </div>
               </>
            )}
         </Listbox>
      </div>
   );
}
